import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Box, Modal as MuiModal, Stack, styled, SxProps, Typography } from "@mui/material";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Button } from "@/components/elements/Button";
import { IconBox } from "@/components/elements/IconBox";
import { IconSave, IconClose } from "@/assets/icons";
import { background, black, divider, text } from "@/theme/colors";
import { toast } from "@/utils/toast";
import { showError } from "@/utils/error";
import { JOB_TAG_COLORS, MAX_JOB_TAGS, MAX_JOB_TAG_LABEL_LENGTH, TJobTagColorKey } from "@/features/job/constants/job-tag-colors";
import { TDeleteTagOutcome, TTag, TTagUsingJob, useTags, useSaveTags, useDeleteTag, validateDeleteTagApi } from "@/api/services/main/tag";
import { JobTagEditorRow } from "@/features/job/components/job-tag-editor/JobTagEditorRow";
import { TEditorRow } from "@/features/job/components/job-tag-editor/job-tag-editor-types";
import { ConfirmDeleteTagModal } from "@/features/job/components/job-tag-editor/ConfirmDeleteTagModal";

type TJobTagEditorModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const WRAPPER_SX: SxProps = { display: "flex", justifyContent: "center", alignItems: "center" };
const HEADER_SX: SxProps = { px: 3, py: 2, borderBottom: `1px solid ${divider.high}` };
const BODY_SX: SxProps = { px: 3, py: 2, overflowY: "auto" };
const FOOTER_SX: SxProps = { px: 3, py: 2, borderTop: `1px solid ${divider.high}` };
const CLOSE_ICON_SX: SxProps = { p: 0 };

const buildRows = (tags: TTag[]): TEditorRow[] => tags.map((t) => ({ key: `db-${t.id}`, id: t.id, label: t.label, color: t.color }));

export const JobTagEditorModal: FC<TJobTagEditorModalProps> = ({ isOpen, onClose }) => {
  const { data: jobTags, refetch: refetchJobTags } = useTags("job");
  const saveJobTags = useSaveTags("job");
  const deleteJobTag = useDeleteTag("job");
  const [rows, setRows] = useState<TEditorRow[]>([]);
  const [seq, setSeq] = useState(0);
  // The existing tag pending delete confirmation (new unsaved rows are removed locally without confirmation)
  const [deleteTarget, setDeleteTarget] = useState<TEditorRow | null>(null);
  const [affectedJobs, setAffectedJobs] = useState<TTagUsingJob[]>([]);
  const [affectedTotal, setAffectedTotal] = useState(0);
  const [affectedValidatedAt, setAffectedValidatedAt] = useState("");
  const [isValidatingDelete, setIsValidatingDelete] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  // Show cached rows immediately on open; force-refetch to pick up other users' changes.
  // rowsRef tracks current rows synchronously so the refetch callback can skip overwrite if the user has already edited.
  const prevOpenRef = useRef(false);
  const rowsRef = useRef<TEditorRow[]>([]);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      const initial = buildRows(jobTags);
      const initialKey = JSON.stringify(initial);
      setRows(initial);
      refetchJobTags().then((res) => {
        if (JSON.stringify(rowsRef.current) === initialKey) setRows(buildRows(res.data?.data ?? []));
      });
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, jobTags, refetchJobTags]);

  const usedColors = useMemo(() => rows.map((r) => r.color), [rows]);

  const nextAvailableColor = (): TJobTagColorKey => {
    const used = new Set(usedColors);
    const found = JOB_TAG_COLORS.find((c) => !used.has(c.key));
    return (found?.key ?? JOB_TAG_COLORS[0].key) as TJobTagColorKey;
  };

  const handleAdd = () => {
    if (rows.length >= MAX_JOB_TAGS) return;
    const key = `new-${seq}`;
    setSeq((s) => s + 1);
    setRows((prev) => [...prev, { key, label: "", color: nextAvailableColor() }]);
  };

  const handleChangeLabel = (key: string, label: string) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, label } : r)));
  const handleChangeColor = (key: string, color: TJobTagColorKey) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, color } : r)));
  const handleDelete = async (key: string) => {
    if (saveJobTags.isPending || isValidatingDelete) return; // Guard against race with bulk-save or double-click during validate
    const target = rows.find((r) => r.key === key);
    if (!target) return;
    if (target.id == null) {
      setRows((prev) => prev.filter((r) => r.key !== key));
      return;
    }
    setIsValidatingDelete(true);
    try {
      const res = await validateDeleteTagApi("job", target.id);
      setAffectedJobs(res.data?.jobs ?? []);
      setAffectedTotal(res.data?.totalCount ?? 0);
      setAffectedValidatedAt(res.data?.validatedAt ?? "");
      setDeleteTarget(target);
    } catch (e) {
      // Consistency error (e.g. tag no longer exists): show error, do not open dialog
      showError(e);
    } finally {
      setIsValidatingDelete(false);
    }
  };

  const closeDeleteConfirm = () => {
    setDeleteTarget(null);
    setAffectedJobs([]);
    setAffectedTotal(0);
    setAffectedValidatedAt("");
  };

  // Returns the delete outcome so the dialog can show newly-tagged jobs (conflict) and retry; only closes on real success.
  const handleConfirmDelete = async (validatedAt: string): Promise<TDeleteTagOutcome> => {
    const target = deleteTarget;
    if (target?.id == null) throw new Error("削除対象が選択されていません。");
    const outcome = await deleteJobTag.mutateAsync({ id: target.id, validatedAt });
    if (outcome.success) {
      setRows((prev) => prev.filter((r) => r.key !== target.key));
      toast.success("タグを削除しました。");
      closeDeleteConfirm();
    }
    return outcome;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setRows((prev) => {
        const oldIndex = prev.findIndex((r) => r.key === active.id);
        const newIndex = prev.findIndex((r) => r.key === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const canSave = rows.every((r) => r.label.trim() !== "");

  const handleSave = async () => {
    if (!canSave) {
      toast.error("ラベルを入力してください。");
      return;
    }
    const labels = rows.map((r) => r.label.trim()); // matches BE validation
    if (new Set(labels).size !== labels.length) {
      toast.error("このラベル名は既に使用されています。");
      return;
    }
    try {
      await saveJobTags.mutateAsync(rows.map((r, i) => ({ id: r.id, label: r.label.trim(), color: r.color, order: i + 1 })));
      toast.success("タグを保存しました。");
      onClose();
    } catch (e) {
      showError(e);
      // BE rolls back all changes on failure; refetch + restore so rows reflect the actual master state.
      const res = await refetchJobTags();
      setRows(buildRows(res.data?.data ?? jobTags));
    }
  };

  const rowKeys = useMemo(() => rows.map((r) => r.key), [rows]);

  return (
    <>
      <MuiModal open={isOpen} onClose={onClose} sx={WRAPPER_SX}>
        <ModalContainer>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={HEADER_SX}>
            <Typography variant="head20Bold">タグの編集</Typography>
            <IconBox onClick={onClose} sx={CLOSE_ICON_SX}>
              <IconClose color={black[80]} fontSize={24} />
            </IconBox>
          </Stack>

          <Stack gap={2} sx={BODY_SX}>
            <Typography variant="cap12" color={text.secondary}>
              ドラッグで並び替え、色枠をクリックでスタイル変更、入力欄でラベルを編集できます。
            </Typography>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={rowKeys} strategy={verticalListSortingStrategy}>
                <Stack gap={1.5}>
                  {rows.map((row) => (
                    <JobTagEditorRow
                      key={row.key}
                      row={row}
                      usedColors={usedColors}
                      maxLabelLength={MAX_JOB_TAG_LABEL_LENGTH}
                      onChangeLabel={handleChangeLabel}
                      onChangeColor={handleChangeColor}
                      onDelete={handleDelete}
                    />
                  ))}
                </Stack>
              </SortableContext>
            </DndContext>

            <Box display="flex" justifyContent="center">
              <Button variant="outline" size="sm" onClick={handleAdd} disabled={rows.length >= MAX_JOB_TAGS}>
                タグの追加
              </Button>
            </Box>
          </Stack>

          <Stack direction="row" justifyContent="flex-end" gap={2} sx={FOOTER_SX}>
            <Button variant="ghost" size="sm" onClick={onClose}>
              キャンセル
            </Button>
            <Button
              size="sm"
              startIcon={<IconSave />}
              onClick={handleSave}
              disabled={!canSave || deleteJobTag.isPending}
              isLoading={saveJobTags.isPending}
            >
              保存
            </Button>
          </Stack>
        </ModalContainer>
      </MuiModal>

      <ConfirmDeleteTagModal
        open={deleteTarget != null}
        onClose={closeDeleteConfirm}
        onOk={handleConfirmDelete}
        isLoading={deleteJobTag.isPending}
        entityType="job"
        tagId={deleteTarget?.id ?? 0}
        tagLabel={deleteTarget?.label ?? ""}
        initialJobs={affectedJobs}
        totalCount={affectedTotal}
        initialValidatedAt={affectedValidatedAt}
      />
    </>
  );
};

const ModalContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 560px;
  max-width: calc(100vw - 32px);
  max-height: 90vh;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  overflow: hidden;
  outline: none;
  &:focus {
    outline: none;
  }
`;
